import { WSPillProps } from "@wingspanhq/fe-component-library";
import { Batch, BatchStatus } from "@wingspanhq/wingspan-api-sdk";

import { selectorIsImportBatchFullyImported } from "./selectorIsImportBatchFullyImported";
import { selectorIsImportBatchPartiallyImported } from "./selectorIsImportBatchPartiallyImported";
import { selectorIsImportBatchUploadFinished } from "./selectorIsImportBatchUploadFinished";
import { selectorIsImportBatchUploadInProgress } from "./selectorIsImportBatchUploadInProgress";

type Args = {
  batch: Batch;
  /** `BatchSummary.progress.total` — undefined while the summary is loading. */
  itemCount?: number;
  /** `BatchSummary.progress.failed` — undefined while the summary is loading. */
  failedCount?: number;
  /** The Flatfile upload job died before finishing. */
  isUploadFailed?: boolean;
  isLoading?: boolean;
  showIcon?: boolean;
};

/**
 * Status pill for an import batch (payables or contractors).
 *
 * Three states have to be derived rather than read off `Batch.status`:
 *
 * - A `Created` batch is "Empty" until rows have been uploaded into it,
 *   "Uploading" while the Flatfile job runs and "Awaiting import" once it ends.
 * - A dead upload job leaves the batch `Created` forever, so "Upload failed"
 *   comes from the job rather than the batch.
 * - The v3 status enum has no `PartiallyCompleted` member, so a batch where
 *   some rows failed still reports `Completed`. Reporting that as an
 *   unqualified success would hide the failures, so the failed count decides.
 */
export const selectorImportBatchStatusPill = (args: Args): WSPillProps => {
  const icon = (name: WSPillProps["icon"]) =>
    args.showIcon ? { icon: name } : {};

  const isUploadInProgress = selectorIsImportBatchUploadInProgress(args.batch);

  const getCreatedBatchPill = (): WSPillProps => {
    if (args.isLoading || args.itemCount === undefined) {
      return {
        theme: "neutral",
        text: args.showIcon ? "" : "--",
        ...icon("loader")
      };
    }

    if (
      selectorIsImportBatchUploadFinished({
        batch: args.batch,
        isEmptyBatch: args.itemCount === 0
      })
    ) {
      return { theme: "warning", text: "Awaiting import", ...icon("alert") };
    }

    return {
      theme: "neutral",
      text: isUploadInProgress ? "Uploading" : "Empty",
      ...icon(isUploadInProgress ? "time" : "block")
    };
  };

  if (isUploadInProgress && args.isUploadFailed) {
    return { theme: "error", text: "Upload failed", ...icon("alert-circle") };
  }

  if (
    selectorIsImportBatchFullyImported({
      batch: args.batch,
      failedCount: args.failedCount
    })
  ) {
    return { theme: "success", text: "Complete", ...icon("check-circle") };
  }

  if (
    selectorIsImportBatchPartiallyImported({
      batch: args.batch,
      failedCount: args.failedCount
    })
  ) {
    return { theme: "warning", text: "Partially imported", ...icon("alert") };
  }

  switch (args.batch.status) {
    case BatchStatus.FAILED:
      return { theme: "error", text: "Import failed", ...icon("alert-circle") };
    case BatchStatus.PENDING:
      return { theme: "blue", text: "Import queued" };
    case BatchStatus.PROCESSING:
      return { theme: "blue", text: "Importing" };
    default:
      return getCreatedBatchPill();
  }
};
